import { PORTFOLIO_BOOK_INITIALIZATION_STATES_V2, PORTFOLIO_BOOK_KINDS_V2, PORTFOLIO_BOOK_STATUSES_V2, PORTFOLIO_SUPPORT_STATES_V2, PORTFOLIO_V2_SCHEMA_VERSION, type PortfolioBookKindV2 } from "./primitives.js";
import { PORTFOLIO_POLICY_ISSUE_LEVELS_V2, PORTFOLIO_POLICY_STATES_V2 } from "./policy.js";
import { PORTFOLIO_POLICY_ASSET_CLASSES_V2, PORTFOLIO_POLICY_FEE_ASSUMPTION_STATES_V2 } from "./policy-definition.js";
import { PORTFOLIO_POLICY_DOLLAR_PREVIEW_STATES_V2, PORTFOLIO_POLICY_DRAFT_VALIDATION_STATES_V2,
  PORTFOLIO_POLICY_LIFECYCLE_ISSUE_CODES_V2, PORTFOLIO_POLICY_LIFECYCLE_ISSUE_FIELDS_V2,
  PORTFOLIO_POLICY_RULE_IDS_V2, type ActivatePortfolioPolicyDraftInputV2, type PortfolioPolicyDraftValidationV2,
  type PortfolioPolicyDraftInputV2, type RevokePortfolioPolicyInputV2, type SavePortfolioPolicyDraftInputV2,
  type ValidatePortfolioPolicyDraftResponseV2 } from "./policy-lifecycle.js";
import { type ActivatePortfolioPolicyDraftResponseV2, PORTFOLIO_POLICY_VALIDATION_EVIDENCE_STATES_V2,
  type PortfolioPolicyLifecycleReadModelV2, type PortfolioPolicyVersionReadModelV2,
  type RevokePortfolioPolicyResponseV2, type SavePortfolioPolicyDraftResponseV2 } from "./policy-lifecycle-read-models.js";
import { isPortfolioPolicyDraftInputV2 } from "./policy-lifecycle-parser.js";
import { isGoverningPortfolioPolicySemanticsV2 } from "./policy-lifecycle-governing-semantics.js";
import { currency, evidenceReference, exact, fingerprint, integer, iso, isoOrNull, issueValue, member,
  nonnegativeDecimal, nullableText, positiveDecimal, revocationEvidence, sameJson, text } from "./policy-lifecycle-read-primitives.js";
import type { CapitalBookSummaryV2 } from "./read-models.js";
const VERSION_KEYS = ["schemaVersion", "policyVersionId", "bookId", "version", "parentVersionId", "state",
  "definitionEvidenceState", "draft", "validation", "validationEvidenceState", "activationEvidence",
  "ownerDecisionId", "effectiveAt", "supersededAt", "createdAt"] as const;
export function isCapitalBookSummaryV2(value: unknown): value is CapitalBookSummaryV2 {
  return exact(value, ["schemaVersion", "workspaceId", "bookId", "kind", "baseCurrency", "status", "initializationState", "version", "activatedAt"])
    && value.schemaVersion === PORTFOLIO_V2_SCHEMA_VERSION && text(value.workspaceId) && text(value.bookId)
    && member(value.kind, PORTFOLIO_BOOK_KINDS_V2) && currency(value.baseCurrency)
    && member(value.status, PORTFOLIO_BOOK_STATUSES_V2) && member(value.initializationState, PORTFOLIO_BOOK_INITIALIZATION_STATES_V2)
    && integer(value.version, 0) && isoOrNull(value.activatedAt);
}

export function isPortfolioPolicyDraftValidationV2(value: unknown): value is PortfolioPolicyDraftValidationV2 {
  if (!exact(value, ["status", "normalizedDraft", "issues", "dollarPreview", "evaluatedAt", "formulaVersion", "inputFingerprint"])
    || !member(value.status, PORTFOLIO_POLICY_DRAFT_VALIDATION_STATES_V2)
    || !isPortfolioPolicyDraftInputV2(value.normalizedDraft) || !Array.isArray(value.issues)
    || !value.issues.every(issue) || !dollarPreview(value.dollarPreview) || !iso(value.evaluatedAt)
    || value.formulaVersion !== "portfolio-policy-lifecycle.v2.2" || !fingerprint(value.inputFingerprint)) return false;
  return value.status !== "READY_FOR_ACTIVATION"
    || value.issues.every((item) => item.level !== "BLOCK" && item.level !== "REJECT");
}

export function isPortfolioPolicyVersionReadModelV2(value: unknown, bookId?: string, bookBaseCurrency?: string): value is PortfolioPolicyVersionReadModelV2 {
  if (!exact(value, VERSION_KEYS) || value.schemaVersion !== PORTFOLIO_V2_SCHEMA_VERSION
    || !text(value.policyVersionId) || !text(value.bookId) || (bookId !== undefined && value.bookId !== bookId)
    || !integer(value.version, 1) || !nullableText(value.parentVersionId) || !member(value.state, PORTFOLIO_POLICY_STATES_V2)
    || !isoOrNull(value.effectiveAt) || !isoOrNull(value.supersededAt) || !iso(value.createdAt)) return false;
  if (value.definitionEvidenceState === "LEGACY_V2_1") return legacyVersion(value);
  if (value.definitionEvidenceState !== "CANONICAL_V2_2" || !isPortfolioPolicyDraftInputV2(value.draft)
    || value.validationEvidenceState !== "SERVER_RECORDED" || !isPortfolioPolicyDraftValidationV2(value.validation)
    || !sameJson(value.draft, value.validation.normalizedDraft)) return false;
  const activated = ["REVIEWED", "ACTIVE", "SUPERSEDED", "REVOKED"].includes(String(value.state));
  if (activated !== (value.activationEvidence !== null) || activated !== text(value.ownerDecisionId)
    || activated !== (value.effectiveAt !== null)) return false;
  if (activated && value.validation.status !== "READY_FOR_ACTIVATION") return false;
  if ((value.state === "SUPERSEDED") !== (value.supersededAt !== null)) return false;
  return !activated || (activationEvidence(value.activationEvidence, value.state, value.draft.capitalBasis, value.effectiveAt)
    && isGoverningPortfolioPolicySemanticsV2({
      draft: value.draft,
      validation: value.validation,
      activationEvidence: value.activationEvidence,
      bookBaseCurrency,
    }));
}

export function isPortfolioPolicyLifecycleReadModelV2(
  value: unknown,
  expectedBookId?: string,
  expectedKind?: PortfolioBookKindV2,
): value is PortfolioPolicyLifecycleReadModelV2 {
  if (!exact(value, ["schemaVersion", "book", "activePolicyVersionId", "latestDraftPolicyVersionId",
    "activePolicy", "latestDraft", "versions", "definitionContext"])
    || value.schemaVersion !== PORTFOLIO_V2_SCHEMA_VERSION || !isCapitalBookSummaryV2(value.book)
    || (expectedBookId !== undefined && value.book.bookId !== expectedBookId)
    || (expectedKind !== undefined && value.book.kind !== expectedKind)
    || !nullableText(value.activePolicyVersionId) || !nullableText(value.latestDraftPolicyVersionId)
    || !Array.isArray(value.versions) || !value.versions.every((item) => isPortfolioPolicyVersionReadModelV2(
      item,
      value.book.bookId,
      value.book.baseCurrency,
    ))
    || !definitionContext(value.definitionContext)) return false;
  const ids = value.versions.map((item) => item.policyVersionId);
  const versions = value.versions.map((item) => item.version);
  if (new Set(ids).size !== ids.length || new Set(versions).size !== versions.length
    || versions.some((version, index) => index > 0 && version <= versions[index - 1]!)) return false;
  if (!lifecycleChains(value.versions)) return false;
  const activeVersions = value.versions.filter((item) => item.state === "ACTIVE");
  if (activeVersions.length !== (value.activePolicyVersionId === null ? 0 : 1)
    || (activeVersions[0]?.policyVersionId ?? null) !== value.activePolicyVersionId) return false;
  if (!pointer(value.activePolicyVersionId, value.activePolicy, value.versions, "ACTIVE", value.book.bookId)) return false;
  if (!pointer(value.latestDraftPolicyVersionId, value.latestDraft, value.versions, "DRAFT", value.book.bookId)) return false;
  const reviewedDraftIds = new Set(value.versions.filter((item) => item.state === "REVIEWED").map((item) => item.parentVersionId));
  const latestUnconsumed = [...value.versions].reverse().find((item) => item.state === "DRAFT"
    && item.definitionEvidenceState === "CANONICAL_V2_2" && !reviewedDraftIds.has(item.policyVersionId)) ?? null;
  return (latestUnconsumed?.policyVersionId ?? null) === value.latestDraftPolicyVersionId;
}

export function isValidatePortfolioPolicyDraftResponseV2(value: unknown, expectedDraft: PortfolioPolicyDraftInputV2): value is ValidatePortfolioPolicyDraftResponseV2 {
  return exact(value, ["ok", "validation"]) && value.ok === true && isPortfolioPolicyDraftValidationV2(value.validation)
    && sameJson(value.validation.normalizedDraft, expectedDraft);
}

export function isSavePortfolioPolicyDraftResponseV2(
  value: unknown,
  input: SavePortfolioPolicyDraftInputV2,
  kind: PortfolioBookKindV2,
): value is SavePortfolioPolicyDraftResponseV2 {
  if (!exact(value, ["ok", "savedPolicy", "lifecycle"]) || value.ok !== true
    || !isPortfolioPolicyVersionReadModelV2(value.savedPolicy, input.bookId) || value.savedPolicy.state !== "DRAFT"
    || value.savedPolicy.definitionEvidenceState !== "CANONICAL_V2_2"
    || !sameJson(value.savedPolicy.draft, input.draft)
    || !isPortfolioPolicyLifecycleReadModelV2(value.lifecycle, input.bookId, kind)
    || value.lifecycle.book.version < input.expectedBookVersion + 1) return false;
  return value.lifecycle.versions.some((item) => item.policyVersionId === value.savedPolicy.policyVersionId
    && sameJson(item, value.savedPolicy));
}

export function isActivatePortfolioPolicyDraftResponseV2(
  value: unknown,
  input: ActivatePortfolioPolicyDraftInputV2,
  kind: PortfolioBookKindV2,
): value is ActivatePortfolioPolicyDraftResponseV2 {
  if (input.ownerConfirmation !== true || !exact(value, ["ok", "reviewedPolicy", "activePolicy", "lifecycle"]) || value.ok !== true
    || !isPortfolioPolicyVersionReadModelV2(value.reviewedPolicy, input.bookId) || value.reviewedPolicy.state !== "REVIEWED"
    || !isPortfolioPolicyVersionReadModelV2(value.activePolicy, input.bookId)
    || (value.activePolicy.state !== "ACTIVE" && value.activePolicy.state !== "SUPERSEDED")
    || value.reviewedPolicy.parentVersionId !== input.policyVersionId
    || value.activePolicy.parentVersionId !== value.reviewedPolicy.policyVersionId
    || value.reviewedPolicy.validation?.inputFingerprint !== input.inputFingerprint
    || value.activePolicy.validation?.inputFingerprint !== input.inputFingerprint
    || !activationInputMatches(value.reviewedPolicy, input)
    || !isPortfolioPolicyLifecycleReadModelV2(value.lifecycle, input.bookId, kind)
    || value.lifecycle.book.version < input.expectedBookVersion + 1) return false;
  const source = value.lifecycle.versions.find((item) => item.policyVersionId === input.policyVersionId);
  const reviewed = value.lifecycle.versions.find((item) => item.policyVersionId === value.reviewedPolicy.policyVersionId);
  const active = value.lifecycle.versions.find((item) => item.policyVersionId === value.activePolicy.policyVersionId);
  return source?.version === input.expectedPolicyVersion && Boolean(reviewed && sameJson(reviewed, value.reviewedPolicy))
    && Boolean(active && sameJson(active, value.activePolicy));
}

export function isRevokePortfolioPolicyResponseV2(
  value: unknown,
  input: RevokePortfolioPolicyInputV2,
  kind: PortfolioBookKindV2,
): value is RevokePortfolioPolicyResponseV2 {
  if (input.ownerConfirmation !== true || !exact(value, ["ok", "revokedPolicy", "lifecycle"]) || value.ok !== true
    || !isPortfolioPolicyVersionReadModelV2(value.revokedPolicy, input.bookId) || value.revokedPolicy.state !== "REVOKED"
    || value.revokedPolicy.parentVersionId !== input.activePolicyVersionId
    || value.revokedPolicy.activationEvidence?.revocation?.reason !== input.reason.trim()
    || !isPortfolioPolicyLifecycleReadModelV2(value.lifecycle, input.bookId, kind)
    || value.lifecycle.book.version < input.expectedBookVersion + 1) return false;
  const parent = value.lifecycle.versions.find((item) => item.policyVersionId === input.activePolicyVersionId);
  const revoked = value.lifecycle.versions.find((item) => item.policyVersionId === value.revokedPolicy.policyVersionId);
  return parent?.version === input.expectedPolicyVersion && Boolean(revoked && sameJson(revoked, value.revokedPolicy));
}

function activationInputMatches(version: PortfolioPolicyVersionReadModelV2, input: ActivatePortfolioPolicyDraftInputV2): boolean {
  const evidence = version.activationEvidence;
  if (!evidence || evidence.ownerDecisionNote !== input.ownerDecisionNote
    || !sameJson(evidence.reviewGovernance.reviewEvidenceRefs, input.reviewEvidenceRefs)) return false;
  const acknowledgements = evidence.reviewGovernance.warningAcknowledgements
    .map(({ issueCode, note }) => ({ issueCode, note }));
  return sameJson(acknowledgements, input.warningAcknowledgements);
}

function pointer(id: unknown, item: unknown, versions: PortfolioPolicyVersionReadModelV2[], state: "ACTIVE" | "DRAFT", bookId: string): boolean {
  if (id === null || item === null) return id === null && item === null;
  if (typeof id !== "string" || !isPortfolioPolicyVersionReadModelV2(item, bookId) || item.policyVersionId !== id || item.state !== state) return false;
  const stored = versions.find((candidate) => candidate.policyVersionId === id);
  return Boolean(stored && sameJson(stored, item));
}

function lifecycleChains(versions: PortfolioPolicyVersionReadModelV2[]): boolean {
  const byId = new Map(versions.map((item) => [item.policyVersionId, item]));
  const childCounts = new Map<string, number>();
  for (const item of versions) {
    if (item.parentVersionId === null) {
      if (item.state !== "DRAFT") return false;
      continue;
    }
    const parent = byId.get(item.parentVersionId);
    if (!parent || parent.version >= item.version) return false;
    if (item.state === "REVIEWED") {
      if (parent.state !== "DRAFT" || !reviewLineage(parent, item)) return false;
      childCounts.set(parent.policyVersionId, (childCounts.get(parent.policyVersionId) ?? 0) + 1);
    }
    if (item.state === "ACTIVE" || item.state === "SUPERSEDED") {
      if (parent.state !== "REVIEWED" || !activationLineage(parent, item)) return false;
      childCounts.set(parent.policyVersionId, (childCounts.get(parent.policyVersionId) ?? 0) + 1);
    }
    if (item.state === "REVOKED") {
      if (parent.state !== "SUPERSEDED" || !revocationLineage(parent, item)) return false;
      childCounts.set(parent.policyVersionId, (childCounts.get(parent.policyVersionId) ?? 0) + 1);
    }
  }
  return [...childCounts.values()].every((count) => count === 1);
}

function legacyVersion(value: Record<string, unknown>): boolean {
  return value.state === "DRAFT" && value.validation === null
    && (value.validationEvidenceState === "LEGACY_DERIVED" || value.validationEvidenceState === "UNAVAILABLE")
    && value.activationEvidence === null && value.effectiveAt === null && value.supersededAt === null
    && text(value.ownerDecisionId) && legacyDraft(value.draft, value);
}

function legacyDraft(value: unknown, wrapper: Record<string, unknown>): boolean {
  return exact(value, ["schemaVersion", "policyVersionId", "bookId", "version", "parentVersionId", "state",
    "legacyDefinitionVersion", "baseCurrency", "capitalBasis", "declaredCapital", "capitalBaseSnapshot",
    "maxOpenRiskPct", "maxTradeRiskPct", "maxPositionPct", "minimumCashReservePct", "minimumRewardToRisk",
    "slippageAllowancePerUnit", "allowFractionalQuantity", "ownerDecisionId", "effectiveAt", "createdAt"])
    && value.schemaVersion === PORTFOLIO_V2_SCHEMA_VERSION && value.legacyDefinitionVersion === "portfolio-policy.v2.1"
    && value.policyVersionId === wrapper.policyVersionId && value.bookId === wrapper.bookId
    && value.version === wrapper.version && value.parentVersionId === wrapper.parentVersionId
    && value.state === "DRAFT" && currency(value.baseCurrency)
    && (value.capitalBasis === "OWNER_DECLARED_CAPITAL" || value.capitalBasis === "CURRENT_EQUITY")
    && positiveDecimal(value.declaredCapital) && value.capitalBaseSnapshot === null
    && [value.maxOpenRiskPct, value.maxTradeRiskPct, value.maxPositionPct, value.minimumCashReservePct,
      value.minimumRewardToRisk, value.slippageAllowancePerUnit].every(nonnegativeDecimal)
    && typeof value.allowFractionalQuantity === "boolean" && value.ownerDecisionId === wrapper.ownerDecisionId
    && iso(value.effectiveAt) && value.createdAt === wrapper.createdAt;
}

function reviewLineage(parent: PortfolioPolicyVersionReadModelV2, child: PortfolioPolicyVersionReadModelV2): boolean {
  return parent.definitionEvidenceState === "CANONICAL_V2_2" && child.definitionEvidenceState === "CANONICAL_V2_2"
    && parent.validation?.status === "READY_FOR_ACTIVATION" && child.validation?.status === "READY_FOR_ACTIVATION"
    && sameJson(parent.draft, child.draft) && parent.validation.inputFingerprint === child.validation.inputFingerprint;
}

function activationLineage(parent: PortfolioPolicyVersionReadModelV2, child: PortfolioPolicyVersionReadModelV2): boolean {
  return parent.definitionEvidenceState === "CANONICAL_V2_2" && child.definitionEvidenceState === "CANONICAL_V2_2"
    && sameJson(parent.draft, child.draft) && sameJson(parent.validation, child.validation)
    && sameJson(parent.activationEvidence, child.activationEvidence)
    && parent.ownerDecisionId === child.ownerDecisionId && parent.effectiveAt === child.effectiveAt;
}

function revocationLineage(parent: PortfolioPolicyVersionReadModelV2, child: PortfolioPolicyVersionReadModelV2): boolean {
  if (parent.definitionEvidenceState !== "CANONICAL_V2_2" || child.definitionEvidenceState !== "CANONICAL_V2_2"
    || !parent.activationEvidence || !child.activationEvidence || child.activationEvidence.revocation === null) return false;
  return sameJson(parent.draft, child.draft) && sameJson(parent.validation, child.validation)
    && sameJson({ ...parent.activationEvidence, revocation: null }, { ...child.activationEvidence, revocation: null })
    && parent.ownerDecisionId === child.ownerDecisionId;
}

function issue(value: unknown): boolean {
  return exact(value, ["code", "copyKey", "level", "ruleId", "ruleVersion", "field", "inputValue", "allowedValue"])
    && member(value.code, PORTFOLIO_POLICY_LIFECYCLE_ISSUE_CODES_V2) && value.copyKey === value.code
    && member(value.level, PORTFOLIO_POLICY_ISSUE_LEVELS_V2) && member(value.ruleId, PORTFOLIO_POLICY_RULE_IDS_V2)
    && value.ruleVersion === "portfolio-policy-rule.v2.2" && member(value.field, PORTFOLIO_POLICY_LIFECYCLE_ISSUE_FIELDS_V2)
    && issueValue(value.inputValue) && issueValue(value.allowedValue);
}

function dollarPreview(value: unknown): boolean {
  if (!exact(value, ["status", "currency", "capitalBaseAmount", "maxOpenRiskAmount", "maxTradeRiskAmount",
    "maxPositionAmount", "maxSectorExposureAmount", "maxCorrelatedExposureAmount", "maxPortfolioDrawdownAmount",
    "minimumCashReserveAmount", "capitalAfterMinimumCashReserve"])) return false;
  return member(value.status, PORTFOLIO_POLICY_DOLLAR_PREVIEW_STATES_V2)
    && (value.currency === null || currency(value.currency))
    && Object.entries(value).every(([key, item]) => ["status", "currency"].includes(key) || item === null || nonnegativeDecimal(item));
}

function definitionContext(value: unknown): boolean {
  if (!exact(value, ["brokerSources", "assetClassSupport"]) || !Array.isArray(value.brokerSources)
    || !Array.isArray(value.assetClassSupport) || !supportSnapshot(value.assetClassSupport)) return false;
  const sources = value.brokerSources;
  return sources.every((item) => exact(item, ["brokerSourceId", "providerName", "maskedAccountRef", "defaultLotMethod", "feeScheduleAvailable"])
    && text(item.brokerSourceId) && text(item.providerName) && nullableText(item.maskedAccountRef)
    && member(item.defaultLotMethod, ["SPECIFIC", "FIFO", "LIFO", "AVERAGE", "UNKNOWN"] as const)
    && typeof item.feeScheduleAvailable === "boolean")
    && new Set(sources.map((item) => item.brokerSourceId)).size === sources.length;
}

function activationEvidence(value: unknown, state: unknown, capitalBasis: unknown, effectiveAt: unknown): boolean {
  if (!exact(value, ["capitalBaseSnapshot", "sourceCostSnapshot", "assetClassSupportSnapshot", "reviewGovernance", "ownerDecisionNote", "revocation"])
    || !sourceCostSnapshot(value.sourceCostSnapshot) || !supportSnapshot(value.assetClassSupportSnapshot)
    || !reviewGovernance(value.reviewGovernance) || !nullableText(value.ownerDecisionNote)) return false;
  const capitalOk = capitalBasis === "CURRENT_EQUITY" ? capitalSnapshot(value.capitalBaseSnapshot) : value.capitalBaseSnapshot === null;
  const revocationOk = state === "REVOKED" ? revocationEvidence(value.revocation) : value.revocation === null;
  return capitalOk && revocationOk && iso(effectiveAt);
}

function capitalSnapshot(value: unknown): boolean {
  return exact(value, ["value", "asOf", "sourceId"]) && positiveDecimal(value.value) && iso(value.asOf) && text(value.sourceId);
}

function sourceCostSnapshot(value: unknown): boolean {
  if (!exact(value, ["brokerSourceId", "feeScheduleRef", "feeEvidenceState"]) || !nullableText(value.brokerSourceId)
    || !nullableText(value.feeScheduleRef) || !member(value.feeEvidenceState, PORTFOLIO_POLICY_FEE_ASSUMPTION_STATES_V2)) return false;
  return value.feeScheduleRef !== null || value.feeEvidenceState === "UNKNOWN";
}

function supportSnapshot(value: unknown): boolean {
  if (!Array.isArray(value) || value.length !== PORTFOLIO_POLICY_ASSET_CLASSES_V2.length) return false;
  if (!value.every((item) => exact(item, ["assetClass", "supportState"])
    && member(item.assetClass, PORTFOLIO_POLICY_ASSET_CLASSES_V2) && member(item.supportState, PORTFOLIO_SUPPORT_STATES_V2))) return false;
  return new Set(value.map((item) => item.assetClass)).size === PORTFOLIO_POLICY_ASSET_CLASSES_V2.length;
}

function reviewGovernance(value: unknown): boolean {
  return exact(value, ["reviewedAt", "reviewEvidenceRefs", "warningAcknowledgements"])
    && iso(value.reviewedAt) && Array.isArray(value.reviewEvidenceRefs) && value.reviewEvidenceRefs.every(evidenceReference)
    && new Set(value.reviewEvidenceRefs.map((item) => item.evidenceId)).size === value.reviewEvidenceRefs.length
    && Array.isArray(value.warningAcknowledgements) && value.warningAcknowledgements.every((item) => exact(item, ["issueCode", "note", "acknowledgedAt"])
      && member(item.issueCode, PORTFOLIO_POLICY_LIFECYCLE_ISSUE_CODES_V2) && nullableText(item.note) && iso(item.acknowledgedAt));
}
